/**
 * Közös összesítő-modell a felolvasó táblához és a rendelés adatlaphoz.
 *
 * Ugyanazokat a szabályokat követi, mint a riport-adatforrás "summary" adatkészlete
 * (api/application/libraries/ElitReportsLib.php::build_summary), hogy a tablet, az adatlap és a
 * nyomtatott méretlap ne fusson szét.
 */

export const ORDER_TYPE_LABELS = { 1: "Vényes", 2: "Telemedicina", 3: "Privát" };
export const MOUNTING_LABELS = { 2: "Fűző", 3: "Csat", 4: "Cúg", 6: "Tépőzár" };
export const SHOEHORN_LABELS = {
    2: "Formás vagy széles gömbölyű orr",
    3: "Vágott orr",
    4: "Neuropathiás / diab",
    5: "Sportos orr",
    6: "Kerek orr",
};

export const SPECIAL_NEED_LABELS = [
    { key: "hmeliorder_spn_nose_cutout", label: "Orr kivágás" },
    { key: "hmeliorder_spn_hole_punch", label: "Díszlyukasztás" },
    { key: "hmeliorder_spn_deco_stitch", label: "Díszöltés" },
    { key: "hmeliorder_spn_tongue", label: "Nyelv" },
    { key: "hmeliorder_eyelet", label: "Fűzőszem" },
    { key: "hmeliorder_clasp", label: "Cipőszár kapcsozás" },
    { key: "hmeliorder_extra_zipper", label: "Extra cipzár" },
];

export const isOn = (value) => Number(value) === 1;

export const hasText = (value) => value !== null && value !== undefined && String(value).trim() !== "" && String(value).trim() !== "-";

export const displayValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
    return value;
};

export const yesNo = (value) => (isOn(value) ? "Igen" : "Nem");

export const formatPrice = (price) =>
    new Intl.NumberFormat("hu-HU", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(price) || 0) + " Ft";

const formatMm = (value) => {
    const n = Number(value);
    if (!n) return "";
    return `${Number.isInteger(n) ? n : n.toFixed(2).replace(/\.?0+$/, "")} mm`;
};

/**
 * Színcsoportok (alkatrész felosztás) a modell konfigjából: az app a model_options-ból,
 * a szerver (riport) a model_color_groups-ból adja, mindkettőt kezeljük.
 */
export const getColorGroups = (order) => {
    const fromModel = order?.model_options?.colors_and_materials_for_model;
    const fromServer = order?.model_color_groups;
    const src = fromModel && Object.keys(fromModel).some((k) => k.startsWith("color_group_")) ? fromModel : fromServer || {};
    return [1, 2, 3].map((i) => (Array.isArray(src[`color_group_${i}`]) ? src[`color_group_${i}`] : []));
};

/**
 * Bőr színek sorai: [{ index, parts, color }] a tárolt "S9 Fekete / Barkás, B21 Sötétzöld / Boksz" szövegből.
 */
export const getColorLines = (order) => {
    const raw = String(order?.hmeliorder?.hmeliorder_leather_color || "");
    const values = raw
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
    const groups = getColorGroups(order);
    return values.map((color, idx) => ({
        index: idx + 1,
        parts: values.length > 1 ? groups[idx] || [] : [],
        color,
    }));
};

/**
 * Donga egy oldalra: "12 mm, belső, teljes hossz" vagy "Nem kell".
 */
export const getStaveText = (hmeliorder, side) => {
    const h = hmeliorder || {};
    const value = Number(h[`hmeliorder_stave_${side}_value`]) || 0;
    const inside = isOn(h[`hmeliorder_stave_${side}_in`]);
    const outside = isOn(h[`hmeliorder_stave_${side}_out`]);
    const total = isOn(h[`hmeliorder_stave_${side}_total`]);
    if (value <= 0 && !inside && !outside && !total) return "Nem kell";
    const parts = [];
    if (value > 0) parts.push(formatMm(value));
    if (inside) parts.push("belső");
    if (outside) parts.push("külső");
    parts.push(total ? "teljes hossz" : "sarok");
    return parts.join(", ");
};

/**
 * Bejelölt speciális igények címkéi (üres tömb, ha nincs).
 */
export const getSpecialNeeds = (hmeliorder) =>
    SPECIAL_NEED_LABELS.filter((item) => isOn(hmeliorder?.[item.key])).map((item) => item.label);

export const getSpecialNeedsText = (hmeliorder) => {
    const needs = getSpecialNeeds(hmeliorder);
    return needs.length ? needs.join(", ") : "Nincs";
};

export const getMountingText = (hmeliorder) => {
    const h = hmeliorder || {};
    if (hasText(h.hmeliorder_mounting_type_national)) return h.hmeliorder_mounting_type_national;
    return MOUNTING_LABELS[Number(h.hmeliorder_mounting_type)] || "-";
};

export const getShoehornText = (hmeliorder) => {
    const h = hmeliorder || {};
    if (hasText(h.hmeliorder_shoehorn)) return h.hmeliorder_shoehorn;
    return SHOEHORN_LABELS[Number(h.hmeliorder_shoehornmodel_id)] || "-";
};

export const getShoeFormText = (hmeliorder) => (isOn(hmeliorder?.hmeliorder_is_hightopshoes) ? "Magas szárú" : "Félcipő");

export const getSoleKind = (hmeliorder) => {
    const t = String(hmeliorder?.hmeliorder_sole_type || "").toLowerCase();
    if (t === "forma") return "forma";
    if (t === "epitett") return "epitett";
    return "";
};

export const getSoleKindText = (hmeliorder) => {
    const kind = getSoleKind(hmeliorder);
    return kind === "forma" ? "Forma talp" : kind === "epitett" ? "Épített talp" : "-";
};

export const getOrderTypeText = (hmeliorder) => ORDER_TYPE_LABELS[Number(hmeliorder?.hmeliorder_order_type)] || "-";

export const isFullPriceAid = (hmeliorder) => isOn(hmeliorder?.hmeliorder_specaidtype_is_fullpriceaid);

export const getSpecAidText = (hmeliorder) => {
    const name = hmeliorder?.hmeliorder_specaidtype_national;
    if (!hasText(name)) return "-";
    return isFullPriceAid(hmeliorder) ? `${name} (100%-os támogatás)` : name;
};

export const getFeeTotal = (venyek) =>
    (venyek || []).reduce(
        (sum, v) => sum + (v.components || []).reduce((s, c) => s + (Number(c.component_computed_price) || 0), 0),
        0
    );

export const getFeeTotalText = (order) => {
    if (isFullPriceAid(order?.hmeliorder)) return "Nincs, teljes támogatás";
    return formatPrice(getFeeTotal(order?.venyek));
};

/**
 * Cipő adatok sorai (címke, érték). A web által nem töltött régi mezők csak akkor jelennek meg, ha van bennük adat.
 */
export const buildShoeRows = (order) => {
    const h = order?.hmeliorder || {};
    const soleKind = getSoleKind(h);
    const rows = [
        { label: "Modell szám", value: displayValue(h.hmeliorder_modell_num) },
        { label: "Cipő forma", value: getShoeFormText(h) },
        { label: "Rögzítés típusa", value: getMountingText(h) },
        { label: "Speciális igények", value: getSpecialNeedsText(h) },
        { label: "Orr forma", value: getShoehornText(h) },
        { label: "Orrmerevítő típusa / anyaga", value: displayValue([h.hmeliorder_nose_brace_type, h.hmeliorder_nose_brace_mater].filter(hasText).join(" / ")) },
        { label: "Talp jellege", value: getSoleKindText(h) },
    ];
    if (soleKind === "epitett") {
        rows.push({ label: "Sarok típusa", value: displayValue(h.hmeliorder_corner_type) });
        rows.push({
            label: "Sarok magassága",
            value: Number(h.hmeliorder_corner_height) > 0 ? `${String(Number(h.hmeliorder_corner_height))} cm` : "-",
        });
    }
    rows.push({ label: "Bélés típusa", value: displayValue(h.hmeliorder_liner_material) });

    // örökölt mezők, csak ha van bennük érték (a web nem tölti őket)
    const legacy = [
        ["Modell megjegyzés", h.hmeliorder_modell_remark],
        ["Kaptafa megjegyzés", h.hmeliorder_shoehorn_remark],
        ["Sarok anyaga", h.hmeliorder_corner_material],
        ["Gyártási eljárás", h.hmeliorder_facturing_process],
        ["Talp anyaga", h.hmeliorder_foot_material],
        ["Cipő megjegyzés", h.hmeliorder_shoes_remark],
        ["Gyártás, ráma, talp megjegyzés", h.hmeliorder_facramafoot_remark],
    ];
    legacy.forEach(([label, value]) => {
        if (hasText(value)) rows.push({ label, value });
    });
    return rows;
};

/**
 * Színek és talp/ráma sorai (címke, érték).
 */
export const buildColorRows = (order) => {
    const h = order?.hmeliorder || {};
    const soleKind = getSoleKind(h);
    const rows = [];
    const stitch = order?.tuzes_szine_szin_name;
    rows.push({ label: "Tűzés színe", value: hasText(stitch) ? stitch : "Megegyezik a bőr színével" });
    rows.push({ label: "Talp jellege", value: getSoleKindText(h) });
    rows.push({ label: "Talp típusa", value: displayValue(h.hmeliorder_rmelisolerama_sole_type_national) });
    rows.push({ label: "Talp színe", value: displayValue(h.hmeliorder_rmelisolerama_sole_color_national) });
    if (soleKind !== "forma") {
        rows.push({ label: "Köztalp színe", value: displayValue(h.hmeliorder_sole_color_national) });
        rows.push({ label: "Ráma típusa", value: displayValue(h.hmeliorder_rama_type_national) });
        rows.push({ label: "Ráma színe", value: displayValue(h.hmeliorder_rama_sole_color_national) });
    }
    return rows;
};

/**
 * Bal/jobb sorok: kéreg, betét, donga, gördülő, sarokemelés, orvosi kód.
 */
export const buildLeftRightRows = (order) => {
    const h = order?.hmeliorder || {};
    return [
        { label: "Kéreg", left: displayValue(h.hmeliorder_rind_material), right: displayValue(h.hmeliorder_rind_type) },
        { label: "Betét", left: displayValue(h.hmeliorder_insole_type_left), right: displayValue(h.hmeliorder_insole_type_right) },
        { label: "Donga", left: getStaveText(h, "left"), right: getStaveText(h, "right") },
        { label: "Donga megjegyzés", left: displayValue(h.hmeliorder_stave_left_remark), right: displayValue(h.hmeliorder_stave_right_remark) },
        { label: "Gördülő", left: displayValue(h.hmeliorder_rolling_left), right: displayValue(h.hmeliorder_rolling_right), unit: "mm" },
        { label: "Sarok emelés", left: displayValue(h.hmeliorder_cornerlift_left), right: displayValue(h.hmeliorder_cornerlift_right), unit: "mm" },
        { label: "Belső ékemelkedés", left: displayValue(h.hmeliorder_midcorner_left), right: displayValue(h.hmeliorder_midcorner_right), unit: "mm" },
        { label: "Cipő orvosi kódja", left: displayValue(h.hmeliorder_shoes_med_left), right: displayValue(h.hmeliorder_shoes_med_right) },
    ];
};

/**
 * Egészségügyi kérdések sorai.
 */
export const buildHealthRows = (order) => {
    const h = order?.hmeliorder || {};
    const rows = [
        { label: "Viselt már ortopéd cipőt?", value: yesNo(h.hmeliorder_is_wearortop) },
        { label: "Rendelt máshol ortopéd cipőt?", value: yesNo(h.hmeliorder_is_orderotherplace) },
        { label: "Allergia", value: yesNo(h.hmeliorder_is_allergie) },
    ];
    if (isOn(h.hmeliorder_is_allergie)) rows.push({ label: "Allergia részletei", value: displayValue(h.hmeliorder_allergie_details) });
    rows.push({ label: "Szedett vízhajtó", value: yesNo(h.hmeliorder_is_takepill) });
    rows.push({ label: "Műtét", value: yesNo(h.hmeliorder_is_doneoper) });
    rows.push({ label: "Csontkinövés", value: yesNo(h.hmeliorder_is_osselet) });
    rows.push({ label: "Érzékeny rész", value: yesNo(h.hmeliorder_is_sensitpart) });
    return rows;
};
